---
title: "The Snapchat API in 2026: what Snap's official APIs return, and how to get follower counts"
description: "Snap's Public Profile API returns follower counts, but only to allowlisted partners. We tested profile lookups and priced three more ways to read them."
date: "2026-11-02"
postType: "alternatives"
author: "Kevin Wang"
tags: ["snapchat", "social-media", "api", "creators", "comparison"]
keyword: "snapchat api"
ogImage: "/og/snapchat-api.png"
---

export const faqItems = [
  {
    q: "Does Snapchat have a public API?",
    a: "Snap publishes several, but none lets you sign up and read other people's profiles. Login Kit, Creative Kit, the Ads API, the Conversions API, and the Ads Gallery API are open to developers. The Public Profile API, the one that returns follower counts, stories, and Spotlights for any public profile, is allowlist only: you send your OAuth client ID to a Snap contact and wait for approval.",
  },
  {
    q: "Can I get someone's Snapchat follower count through an API?",
    a: "Yes, if the account has a Public Profile and shows its count. Snap's Public Profile API returns it to approved partners as subscriber_count. Third-party APIs read the same public figure from a username, without Snap's approval.",
  },
  {
    q: "Why does a Snapchat profile show no follower count?",
    a: "The owner turned it off. Snapchat lets anyone with a Public Profile toggle Show Follower Count on or off, and a hidden count stays hidden from every API that reads the public profile.",
  },
  {
    q: "Does Login Kit return follower counts?",
    a: "No. Login Kit returns the display name, an external ID, and the Bitmoji avatar of the person who logs in to your app, and Snap's docs say it doesn't give access to messages, shared content, or contacts.",
  },
];

<Callout type="tip" title="Key findings">

- **Snap's official API does return follower counts, but only to allowlisted partners.** The Public Profile API reads any public profile's count, saved stories, and Spotlights, and the way in is a Snap contact who approves your app.
- **Snap's other APIs don't return creator profiles.** Login Kit covers the person logging in to your app, Creative Kit shares into Snapchat, and the Ads and Conversions APIs work on your own ad account.
- **Without approval, a per-call profile API reads the same public numbers.** [AnyAPI](https://getanyapi.com/api/snapchat/profile) returned the follower count for every profile I tried that shows one, matching Snapchat's own page each time, in about 2 seconds a lookup.
- **For Spotlight views and comments, use ScrapeCreators or Apify.** Both read Spotlight videos as well as the profile.

</Callout>

Maybe you're vetting creators for a campaign and need their Snapchat audience next to their TikTok and Instagram numbers. Or you report on a brand's growth every month, and Snapchat is the one network you still copy numbers from by hand.

I read Snap's developer docs on October 3, 2026, looked up creators from a few thousand followers to 29 million through AnyAPI's Snapchat endpoint, and priced three more options from their own pages.

Here's what each one is for.

## The best Snapchat APIs

- [Snap Public Profile API](#best-snapchat-api-for-approved-partners-running-creator-campaigns) for approved partners running creator campaigns
- [AnyAPI](#best-snapchat-api-for-follower-counts-billed-per-lookup) for follower counts billed per lookup
- [ScrapeCreators](#best-snapchat-api-for-spotlight-views-and-comments) for Spotlight views and comments
- [SocialCrawl](#best-snapchat-api-for-one-schema-across-69-platforms) for one schema across 69 platforms
- [Apify Snapchat Scraper](#best-snapchat-api-for-scheduled-exports-with-spotlight-videos) for scheduled exports with Spotlight videos

## What makes a good Snapchat API?

I'm assuming you're a developer or a marketer with a script who needs public profile data for accounts you don't own: a creator's follower count, their bio and website, and what they've posted. Here's what I compared.

- **Access you can actually get.** An API that needs a Snap contact doesn't help if you don't have one.
- **Lookup by username.** You usually start with a handle from a creator's bio or a spreadsheet.
- **Which fields come back.** Follower counts, saved stories, and Spotlight views aren't all in every response.
- **Price at your volume.** Prices below are for 10,000 profile lookups a month, or the vendor's closest unit.

<Callout type="note" title="How this was tested, and its limits">

- AnyAPI: 30 profile lookups on 2026-10-03, one at a time. Most were public profiles that show a count, from `jonrettinger` at 5,200 to `kyliejenner` at 29 million, plus a few that hide it, one personal account, one made-up name, and one profile sent as a URL.
- I checked each count against the same profile's page on snapchat.com that day, logged out. The speeds come from a small run, so treat them as rough.
- Snap's Public Profile API wasn't called, because it needs an allowlisted app. Its section is from Snap's developer docs, read on 2026-10-03.
- ScrapeCreators, SocialCrawl, and Apify weren't run. Their sections are from their own pages on 2026-10-03.

</Callout>

## The best Snapchat APIs at a glance

| Tool | Best for | Why pick it | Pricing at 10,000 lookups a month |
| --- | --- | --- | --- |
| Snap Public Profile API | Approved partners running creator campaigns | Snap's own data, with creator search by follower count and opt-in audience insights | No published price; allowlist only |
| AnyAPI | Follower counts billed per lookup | Profile, follower count, and saved stories from a username or URL | <Price sku="snapchat.profile" calls={10000} /> (<Price sku="snapchat.profile" /> per 1,000 lookups) |
| ScrapeCreators | Spotlight views and comments | Profile with Spotlight stats, plus Spotlight and comment endpoints | $47 Freelance pack (25,000 credits, 1 credit a profile, never expire) |
| SocialCrawl | One schema across 69 platforms | Profile and Spotlight comments in the same shape as its other platforms | £49 Growth pack (20,000 credits, 1 credit a profile, never expire) |
| Apify Snapchat Scraper | Scheduled exports with Spotlight videos | One dataset row per profile, with stories and Spotlight videos | $2.00 per 1,000 profiles, plus $0.001 a run |

## Best Snapchat API for approved partners running creator campaigns

### Snap Public Profile API

<Figure src="/og/screenshots/snapchat-api-snap-allowlist.png" alt="Snap for Developers, Public Profile API Get Started page. Step 2, Activate access to the Public Profile API: The Public Profile API is currently allowlist only. Once you have created your OAuth app, send just the client ID to your Snap contact, and they will coordinate allowlisting your app. Access Request: This API requires allow listing to access. Once you have obtained app credentials, send an email to your Snap point of contact with your OAuth client id and a description of your intended use" caption="Snap's Public Profile API docs on October 3, 2026: allowlist only, through your Snap contact." width={1280} height={900} />

**Snap Public Profile API pros:**

- Snap's own data: follower counts, saved stories, Spotlights, and Lenses for any public profile
- A discover endpoint that searches creators by follower count, country, and audience
- Deeper stats and audience breakdowns for creators who opt in or authorize your app

**Snap Public Profile API cons:**

- Allowlist only: you need a Snap contact to approve your app
- Profiles are fetched by Snap's profile ID, so a username takes a search call first
- Discover results stop at 5,000 creators per query

**This is the official answer to "is there a Snapchat follower count API".** Snap's [Public Profile API](https://developers.snap.com/marketing-api/Public-Profile-API/Introduction) has public endpoints that "do not require profile owner authorization" and return "basic metadata and stats about any Public Profile on Snapchat". Its [FAQ](https://developers.snap.com/marketing-api/Public-Profile-API/FAQ) says what's public: subscriber stats for a profile, and shares and views for Spotlights.

Getting in takes three steps. You create a business account in Snap's Ads Manager, generate an OAuth app under Business Details (Snap warns that a Developer Portal app "will not work with the Public Profile API"), then email your Snap contact the client ID and what you plan to do with it. The [Get Started page](https://developers.snap.com/marketing-api/Public-Profile-API/GetStarted) doesn't say how long approval takes or who qualifies.

Once you're in, a profile comes back with `subscriber_count`, category, country, and logo URLs, and separate public endpoints list its saved stories, Spotlights, and Lenses. The [discover endpoint](https://developers.snap.com/marketing-api/Public-Profile-API/CreatorDiscovery) filters by minimum and maximum followers, creator country, audience gender, and paid-partnership history, and returns up to 5,000 creators per query. Snap's own example responses show counts of 409 and 210, not round hundreds.

What about Snap's other APIs? None of them reads a creator's profile:

| Snap API | What it returns |
| --- | --- |
| [Login Kit](https://developers.snap.com/snap-kit/login-kit/overview) | The display name, an external ID, and the Bitmoji avatar of the person who logs in to your app. Snap says it doesn't give access to messages, shared content, or contacts. |
| [Creative Kit](https://developers.snap.com/snap-kit/creative-kit/overview) | Nothing to read. It sends images, videos, stickers, captions, and Lenses from your app into Snapchat. |
| [Ads API](https://developers.snap.com/marketing-api/Ads-API/introduction) | Your own campaigns, ad squads, creatives, audiences, and performance stats. |
| [Conversions API](https://developers.snap.com/marketing-api/Conversions-API) | Nothing to read. You send conversion events to Snap for ad attribution. |
| [Ads Gallery API](https://developers.snap.com/marketing-api/Ads-Gallery-Api/Introduction) | Ads that ran in the EU in the last 12 months, with advertiser name and dates. No OAuth token needed. |
| Story Kit | Not in Snap's developer docs as of October 2026. |

**Snap Public Profile API pricing:** no price published. Access is by allowlist through a Snap contact. Source: [Get Started](https://developers.snap.com/marketing-api/Public-Profile-API/GetStarted), October 3, 2026.

## Best Snapchat API for follower counts billed per lookup

### AnyAPI

<Figure src="/og/charts/snapchat-api-fields.svg" alt="Bar chart of how many of 28 Snapchat profiles returned each field on October 3, 2026: display name and avatar 28 of 28; subscriber count 24 of 28; saved stories 18 of 28; category 16 of 28; bio 15 of 28; website 15 of 28. The 4 without a count hide it on Snapchat's own page too" caption="What 28 public profiles returned, October 3, 2026. Every profile that shows a follower count returned it." width={760} height={524} />

**AnyAPI pros:**

- A follower count for all 24 public profiles that show one, from 5,200 to 29 million, each matching Snapchat's own page
- Saved stories with titles and thumbnails, up to 17 a profile
- Takes a username or a full profile URL, billed per call from a prepaid wallet

**AnyAPI cons:**

- One endpoint: the profile and its saved stories, without Spotlight videos or view counts
- Counts are the rounded figure Snapchat shows the public

[AnyAPI](https://getanyapi.com/api/snapchat/profile) is the pick if you have a list of usernames and need follower counts without asking Snap for access. You send a username to `snapchat.profile` and get the profile back as one item.

```bash
curl -sS -X POST 'https://api.getanyapi.com/v1/run/snapchat.profile' \
  -H "Authorization: Bearer $ANYAPI_API_KEY" \
  -H 'Content-Type: application/json' \
  -d '{"username":"nasa"}'
```

Here's what came back for NASA on 2026-10-03, trimmed to two of its 10 saved stories:

```json
{
  "output": {
    "found": true,
    "data": {
      "items": [
        {
          "handle": "nasa",
          "displayName": "NASA",
          "url": "https://www.snapchat.com/add/nasa",
          "subscribers": 756100,
          "category": "Government Org",
          "bio": "Explore the universe and discover our home planet with official NASA snaps.",
          "website": "https://www.nasa.gov",
          "image": "https://cf-st.sc-cdn.net/aps/bolt/aHR0cHM6Ly9jZi1zdC5zYy1jZG4ubmV0L2QvUVg2VHZkaDBHNUMzcXVidWhWaTJhP2JvPUVnMGFBQm9BTWdFRVNBSlFHV0FCJnVjPTI1._RS0,90_FMjpeg",
          "stories": [
            { "id": "ddf02468-ae16-4fc6-b1bc-bde7e1e22218", "storyTitle": "Moon Joy", "thumbnailUrl": "https://cf-st.sc-cdn.net/d/wAUDrJs74QBAl504N3DhT.410?mo=GjUaFDIBBDoBfUIGCMWw5M4GSAJQXmABUMUBWhBEZkxhcmdlVGh1bWJuYWlsogEHCJoDIgISAA%3D%3D&uc=94" },
            { "id": "7479819b-d2de-44ff-a394-8db45a27c8d6", "storyTitle": "Full Capsule", "thumbnailUrl": "https://cf-st.sc-cdn.net/d/C7eHkq29ayMBY0xtSF9ej.410.IRZXSOY?mo=Gj4aFDIBBDoBfUIGCJT73s4GSAJQXmABUMUBWhBEZkxhcmdlVGh1bWJuYWlsogEQCJoDIgsSACoHSVJaWFNPWQ%3D%3D&uc=94" }
          ]
        }
      ]
    }
  },
  "costUsd": 0.0012,
  "items": 1
}
```

Every call came back, usually in about 2 seconds and never more than 6. The test cost $0.036. {/* receipt 2026-10-03 */}

| What I sent on 2026-10-03 | Profiles | What came back |
| --- | --- | --- |
| Public profiles that show a count, 5,200 to 29 million | 24 (one sent as a URL) | Every count, each equal to the figure on the profile's snapchat.com page |
| Public profiles that hide their count (CNN, Billboard, Michelle Obama, Daily Question) | 4 | The profile, with `subscribers` left out |
| A personal account (`espn`) and a made-up name | 2 | `found: false` |

**A hidden count comes back missing, so don't read it as zero.** Snapchat lets profile owners turn off [Show Follower Count](https://help.snapchat.com/hc/en-us/articles/7012366438676-How-do-I-show-my-follower-count-on-my-Public-Profile), and CNN's page showed no count either. Every returned count was a round hundred, the same figure snapchat.com showed. A personal account without a Public Profile returns `found: false`, the same as a name that doesn't exist.

`stories` holds the saved stories pinned to the profile, not the 24-hour Story. NASA's 10 matched the titles on its profile page, and most profiles had at least one. For a one-off look at a profile without writing code, the free [Snapchat Story Viewer](/tools/snapchat-story-viewer) runs the same lookup in your browser.

**AnyAPI pricing:** <Price sku="snapchat.profile" /> per 1,000 profile lookups (<Price sku="snapchat.profile" per="call" /> each). A call the cheapest source can't answer goes to a backup, which costs up to <Price sku="snapchat.profile" failover /> per 1,000. Prepaid, no plan; new accounts get free credit, no card required.

## Best Snapchat API for Spotlight views and comments

### ScrapeCreators

<Figure src="/og/screenshots/snapchat-api-scrapecreators-pricing.png" alt="ScrapeCreators pricing: Free $0 with 100 credits and up to 7,000 bonus credits to claim; Freelance $47 pay as you go for 25,000 credits, $1.88 per 1k requests; Business $497 for 500,000 credits, $0.99 per 1k requests; Enterprise custom for 1M+ credits. All 38+ APIs included, credits never expire, no credit card required" caption="ScrapeCreators' credit packs on October 3, 2026: $47 for 25,000 credits, and a Snapchat profile is 1 credit." width={1280} height={900} />

**ScrapeCreators pros:**

- Three Snapchat endpoints: profile, a Spotlight by link, and a Spotlight's comments
- Its profile response includes Spotlight descriptions with view, share, and comment counts
- 100 free credits, no card, and credits never expire

**ScrapeCreators cons:**

- Prepaid packs: the first paid one is $47
- Counts come back as strings, so you convert them yourself

If Spotlight is the part you care about, [ScrapeCreators](https://scrapecreators.com/snapchat-api) is the pick. You send a GET with an `x-api-key` header to `/v1/snapchat/profile?handle=...`, at [1 credit a request](https://docs.scrapecreators.com/v1/snapchat/profile), and its [own write-up](https://scrapecreators.com/blog/snapchat-api-followers-stories) shows the response: `subscriberCount`, category, whether a Story is live, and a `spotlightStoryMetadata` list with each Spotlight's views, shares, and comments.

Two more endpoints take a Spotlight URL: one for the video's data and one for its comments. If you already use ScrapeCreators for TikTok or Instagram, Snapchat comes with the same key, and the $47 pack covers two and a half months at 10,000 lookups.

**ScrapeCreators pricing:** 100 free credits. Freelance is $47 for 25,000 credits ($1.88 per 1,000 requests) and Business $497 for 500,000 ($0.99 per 1,000). A Snapchat profile is 1 credit, and cached results cost nothing. Source: [scrapecreators.com](https://scrapecreators.com/#pricing), October 3, 2026.

## Best Snapchat API for one schema across 69 platforms

### SocialCrawl

<Figure src="/og/screenshots/snapchat-api-socialcrawl-endpoints.png" alt="SocialCrawl's Snapchat page: What Snapchat endpoints does SocialCrawl ship? 2 live read endpoint for public Snapchat profile data. Profile, 1 credit, /v1/snapchat/profile, Get Snapchat user profile, handle. Spotlight Comments, 1 credit, /v1/snapchat/spotlight/comments, List comments on a Snapchat Spotlight, url and cursor. Below, the profile endpoint returns display name, Bitmoji avatar URL, subscriber count, and bio" caption="SocialCrawl's two Snapchat endpoints on October 3, 2026, at 1 credit each." width={1280} height={900} />

**SocialCrawl pros:**

- Profile and Spotlight comments at 1 credit each
- The same Author and Comment shape as its other 69 platforms
- Its page says cache hits cost nothing and empty or failed calls are refunded

**SocialCrawl cons:**

- Priced in pounds
- Two Snapchat endpoints, with no stories or Spotlight videos listed

[SocialCrawl](https://www.socialcrawl.dev/platforms/snapchat) lists two Snapchat endpoints: a profile that returns "display name, Bitmoji avatar URL, subscriber count, and bio", and a Spotlight's comments. Both come back in the same envelope as every other platform it covers, so a Snapchat creator looks like a TikTok or YouTube creator in your code.

Pick it if your app already reads several networks and you'd rather not write another parser. If Snapchat is all you need, the other options return more of it per call.

**SocialCrawl pricing:** 100 free credits. Starter is £15 for 2,500 credits, Growth £49 for 20,000, and Pro £299 for 150,000, one-time and never expiring. Snapchat endpoints are 1 credit each. Sources: [pricing](https://www.socialcrawl.dev/pricing) and [Snapchat page](https://www.socialcrawl.dev/platforms/snapchat), October 3, 2026.

## Best Snapchat API for scheduled exports with Spotlight videos

### Apify Snapchat Scraper

<Figure src="/og/screenshots/snapchat-api-apify-pricing.png" alt="The Apify Store pricing tab for Snapchat Scraper by Tri Angle, maintained by Apify: Scrape public Snapchat profiles by username or URL: subscriber count, bio, and website, plus their stories and Spotlight videos with views and media links. One row per profile. Profile, $2.00 per 1,000; Actor Start $0.001; platform usage included, free. 1.9K total users, 91 monthly active users, rating 5.0 from 5 reviews" caption="The actor's Apify Store pricing on October 3, 2026: $2.00 per 1,000 profiles, plus $0.001 to start each run." width={1280} height={900} />

**Apify Snapchat Scraper pros:**

- One row per profile with follower count, bio, website, stories, and Spotlight videos with views and media links
- Runs on a schedule and writes to a dataset you export as JSON or CSV
- Busy and steady: nearly every public run in the last 30 days succeeded, per Apify's store

**Apify Snapchat Scraper cons:**

- A run is a batch job, so your code waits for it to finish
- Built for lists of profiles; a single live lookup still starts a run

Tri Angle's [Snapchat Scraper](https://apify.com/tri_angle/snapchat-scraper) is the most used Snapchat actor on Apify's store, with about 2,100 users, and Apify maintains it. You start a run with a list of usernames or URLs, and it writes one row per profile with the Spotlight videos included.

It suits a nightly or weekly export of a creator list into a spreadsheet or warehouse, especially if you want Spotlight views in the same row. At 10,000 profiles a month, the per-profile fee comes to $20 plus a tenth of a cent per run.

**Apify Snapchat Scraper pricing:** $2.00 per 1,000 profiles plus $0.001 per run start, with platform usage included. Source: [Apify Store](https://apify.com/tri_angle/snapchat-scraper/pricing), October 3, 2026.

## Which Snapchat API should you use?

- You have a Snap contact and run creator campaigns at scale: ask for the Public Profile API.
- You need follower counts and saved stories for a list of usernames, pay as you go: AnyAPI.
- You need Spotlight views, shares, and comments from code: ScrapeCreators.
- Your app reads many social networks and you want one schema: SocialCrawl.
- You want a scheduled export of a creator list, Spotlight videos included: Apify's Snapchat Scraper.

Whichever you pick, store a missing follower count as missing, because it means the owner hid it. The [Snapchat API page](/api/snapchat) lists AnyAPI's Snapchat endpoint, and the [Instagram](/api/instagram) and [TikTok](/api/tiktok) pages cover the same creators on the networks you're probably comparing.

<FAQ items={faqItems} />

<CTA to="/api/snapchat/profile" note={<>A Snapchat profile and its follower count as clean JSON, <Price sku="snapchat.profile" per="call" /> a lookup, no plan and nothing that expires. New accounts get free credit, no card required.</>}>
  Try the Snapchat Profile API
</CTA>

Use data responsibly and follow AnyAPI's [Acceptable Use Policy](/acceptable-use).
